import type { GameSettings, GameState } from './types';
import { GAME_VERSION, hydrateState, serializeState } from './simulation';

const DB_NAME = 'crush-season';
const DB_VERSION = 1;
const STORE_NAME = 'saves';
const PRIMARY_KEY = 'autosave';
const BACKUP_KEY = 'backup';

export interface SaveEnvelope {
  schemaVersion: 1;
  gameVersion: string;
  savedAt: number;
  settings: GameSettings;
  state: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Unable to open the save database.'));
  });
}

async function readKey(key: string): Promise<SaveEnvelope | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve((request.result as SaveEnvelope | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('Unable to read save data.'));
    transaction.oncomplete = () => database.close();
  });
}

async function writeKeys(entries: [string, SaveEnvelope][]): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    entries.forEach(([key, value]) => store.put(value, key));
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => reject(transaction.error ?? new Error('Unable to write save data.'));
  });
}

export function makeEnvelope(state: GameState, savedAt = Date.now()): SaveEnvelope {
  const prepared = { ...state, lastSavedAt: savedAt };
  return {
    schemaVersion: 1,
    gameVersion: GAME_VERSION,
    savedAt,
    settings: prepared.settings,
    state: serializeState(prepared)
  };
}

export function parseEnvelope(value: unknown): { state: GameState; envelope: SaveEnvelope } {
  if (!value || typeof value !== 'object') throw new Error('This file is not a Crush Season save.');
  const envelope = value as Partial<SaveEnvelope>;
  if (envelope.schemaVersion !== 1 || typeof envelope.state !== 'string' || typeof envelope.savedAt !== 'number') {
    throw new Error('The save envelope is incomplete or unsupported.');
  }
  const state = hydrateState(JSON.parse(envelope.state));
  return { state, envelope: envelope as SaveEnvelope };
}

export async function hasSave(): Promise<boolean> {
  try {
    return (await readKey(PRIMARY_KEY)) !== null || (await readKey(BACKUP_KEY)) !== null;
  } catch {
    return false;
  }
}

export async function saveGame(state: GameState): Promise<void> {
  const primary = await readKey(PRIMARY_KEY);
  const next = makeEnvelope(state);
  const entries: [string, SaveEnvelope][] = [[PRIMARY_KEY, next]];
  if (primary) entries.push([BACKUP_KEY, primary]);
  await writeKeys(entries);
}

export async function loadGame(): Promise<{ state: GameState; recoveredFromBackup: boolean } | null> {
  const primary = await readKey(PRIMARY_KEY);
  if (primary) {
    try {
      return { state: parseEnvelope(primary).state, recoveredFromBackup: false };
    } catch {
      // Keep the malformed primary untouched; a valid backup may still recover the player.
    }
  }
  const backup = await readKey(BACKUP_KEY);
  if (!backup) return null;
  return { state: parseEnvelope(backup).state, recoveredFromBackup: true };
}

export function exportSave(state: GameState): void {
  const blob = new Blob([JSON.stringify(makeEnvelope(state), null, 2)], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `crush-season-vintage-${state.vintage}.json`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

export async function importSave(file: File): Promise<GameState> {
  const text = await file.text();
  const parsed = parseEnvelope(JSON.parse(text));
  await saveGame(parsed.state);
  return parsed.state;
}

export async function clearSaves(): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).clear();
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => reject(transaction.error ?? new Error('Unable to clear saves.'));
  });
}
